import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Project, isClientRole } from '../types';
import { API_CONFIG } from '../config';
import { apiFetch } from '../utils/api';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';
import { getCurrencySymbol } from '../utils/currency';
import ConfirmationModal from './ui/ConfirmationModal';
import { getUserPreferences, subscribeUserPreferences, updateUserPreferences } from '../utils/userPreferences';

interface Transaction {
  id: string;
  projectId: string;
  date: string;
  amount: number;
  type: 'IN' | 'OUT' | 'LIABILITY';
  category: 'CLIENT_DEPOSIT' | 'ARTIST_PAYOUT' | 'SOFTWARE_HARDWARE' | 'OTHER';
  description: string;
  artistName?: string; // Name of the artist for salary/fee transactions
  createdAt: string;
  updatedAt: string;
}

interface Liability {
  id: string;
  projectId: string;
  date: string;
  amount: number;
  description: string;
  expenseAssignmentId: string | null;
  createdAt: string;
  specialization: string | null;
  estimatedHours: number | null;
  hourlyRate: number | null;
  fixedAmount: number | null;
  shotId: string | null;
  shotName: string | null;
  shotNumber: number | null;
  artistId: string | null;
  artistName: string | null;
}

interface ProjectBalance {
  projectId: string;
  projectName?: string;
  totalIncome: number;
  totalExpense: number;
  pendingLiabilities: number;
  taxReserve: number;
  taxRate?: number; // Tax rate from project settings
  balance: number; // Net Cash
  currency?: string;
  revenueGrandTotal?: number; // Grand Total from approved revenue bidding sheet
  producerName?: string; // Producer name from bidding sheet settings
  coordinatorName?: string; // Coordinator name from bidding sheet settings
  supervisorName?: string; // Supervisor name from bidding sheet settings
  finderName?: string; // Finder name from bidding sheet settings
  paidProfit?: number; // Paid profit distributions
  pendingProfit?: number; // Pending profit distributions (draft)
  availableProfit?: number; // Available profit for distribution
  totalProfit?: number; // Total expected profit
}

interface TransactionsPageProps {
  projects: Project[];
  currentRole: string | null;
  currentUserId?: string;
}

const FINANCE_PROJECT_KEY = 'spellbook_finance_selected_project';

const TransactionsPage: React.FC<TransactionsPageProps> = ({
  projects,
  currentRole,
  currentUserId
}) => {
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    const prefs = getUserPreferences();
    if (prefs.financeSelectedProjectId) return prefs.financeSelectedProjectId;
    const saved = localStorage.getItem(FINANCE_PROJECT_KEY);
    return saved || '';
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [balance, setBalance] = useState<ProjectBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [payingLiability, setPayingLiability] = useState<Liability | null>(null);
  const [payAmount, setPayAmount] = useState('');
  
  // Pay All modal states
  const [isPayAllModalOpen, setIsPayAllModalOpen] = useState(false);
  const [payAllItems, setPayAllItems] = useState<Liability[]>([]);
  const [payAllArtistName, setPayAllArtistName] = useState('');
  const [isPayingAll, setIsPayingAll] = useState(false);
  
  // Delete Liability modal states
  const [isDeleteLiabilityModalOpen, setIsDeleteLiabilityModalOpen] = useState(false);
  const [deletingLiability, setDeletingLiability] = useState<Liability | null>(null);
  const [isDeletingLiability, setIsDeletingLiability] = useState(false);
  
  // Delete Transaction modal states
  const [isDeleteTransactionModalOpen, setIsDeleteTransactionModalOpen] = useState(false);
  const [deletingTransactionId, setDeletingTransactionId] = useState<string | null>(null);
  const [isDeletingTransaction, setIsDeletingTransaction] = useState(false);
  
  // Revert Payment modal states
  const [isRevertPaymentModalOpen, setIsRevertPaymentModalOpen] = useState(false);
  const [revertingPayment, setRevertingPayment] = useState<Liability | null>(null);
  const [isRevertingPayment, setIsRevertingPayment] = useState(false);
  
  // Collapsible sections
  const [isTransactionsExpanded, setIsTransactionsExpanded] = useState(true);
  const [isManagementFeesExpanded, setIsManagementFeesExpanded] = useState(true);
  const [isArtistPaymentsExpanded, setIsArtistPaymentsExpanded] = useState(true);
  
  // Filter states
  const [filterType, setFilterType] = useState<'ALL' | 'IN' | 'OUT'>('ALL');
  const [filterCategory, setFilterCategory] = useState<'ALL' | Transaction['category']>('ALL');
  
  // Liabilities grouping and view
  const [liabilityGroupBy, setLiabilityGroupBy] = useState<'shot' | 'artist' | 'artist-summary'>('artist-summary');
  const [liabilityView, setLiabilityView] = useState<'pending' | 'paid'>('pending');
  
  // Transaction grouping (collapse similar transactions)
  const [groupSimilarTransactions, setGroupSimilarTransactions] = useState(true);
  const [paidLiabilities, setPaidLiabilities] = useState<Liability[]>([]);
  
  // Profit Distribution states
  const [isProfitSectionExpanded, setIsProfitSectionExpanded] = useState(true);
  const [profitDistributions, setProfitDistributions] = useState<any[]>([]);
  const [availableProfit, setAvailableProfit] = useState<{
    revenueGrandTotal: number;
    expenseTotal: number;
    producerFee: number;
    coordinatorFee: number;
    supervisorFee: number;
    finderFee: number;
    taxReserve: number;
    alreadyDistributed: number;
    availableProfit: number;
  } | null>(null);
  const [isProfitModalOpen, setIsProfitModalOpen] = useState(false);
  const [profitPreset, setProfitPreset] = useState<'EQUAL_MANAGERS' | 'EQUAL_ARTISTS' | 'EQUAL_ALL' | 'CUSTOM'>('EQUAL_ALL');
  const [profitAmount, setProfitAmount] = useState('');
  const [profitParticipants, setProfitParticipants] = useState<Array<{type: string; id: string; name: string; percentage: number}>>([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  
  // Delete Distribution confirmation modal
  const [deleteDistributionModal, setDeleteDistributionModal] = useState<{
    isOpen: boolean;
    distributionId: string;
    isDistributed: boolean;
  }>({ isOpen: false, distributionId: '', isDistributed: false });
  

  // Form states
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formAmount, setFormAmount] = useState('');
  const [formType, setFormType] = useState<'IN' | 'OUT'>('IN');
  const [formCategory, setFormCategory] = useState<Transaction['category']>('CLIENT_DEPOSIT');
  const [formDescription, setFormDescription] = useState('');

  // Super Admin can edit all, Sales can edit their own projects
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  const canEdit = currentRole === 'Super Admin' || 
    (currentRole === 'Sales' && selectedProject?.producerId === currentUserId);

  // Filter projects based on role
  // Only show projects that have an approved bidding sheet (sent to finance)
  const availableProjects = projects.filter(p => {
    // First: must have an approved bidding sheet to appear in finance
    if (!p.approvedBiddingSheetName) {
      return false;
    }
    
    // Super Admin sees all projects with approved sheets
    if (currentRole === 'Super Admin') {
      return true;
    }
    // Sales only sees their own projects (where they are producer)
    if (currentRole === 'Sales') {
      return p.producerId === currentUserId;
    }
    // Client/Demo only sees their own projects
    if (isClientRole(currentRole) && p.clientId !== currentUserId && p.clientProfileId !== currentUserId) {
      return false;
    }
    // Other roles: only show projects with approved expenses (sent to finance)
    return p.hasApprovedExpenses === true;
  });

  // Memoize available project IDs to avoid triggering effects on every render
  const availableProjectIds = useMemo(() => 
    availableProjects.map(p => p.id).join(','), 
    [availableProjects]
  );

  // Validate saved project on mount only (once when availableProjects first load)
  const initialValidationDone = useRef(false);
  
  useEffect(() => {
    if (availableProjects.length > 0 && !initialValidationDone.current) {
      initialValidationDone.current = true;
      const saved = localStorage.getItem(FINANCE_PROJECT_KEY);
      if (saved && availableProjects.some(p => p.id === saved)) {
        // Saved project is valid, use it only if not already set
        if (!selectedProjectId) {
          setSelectedProjectId(saved);
        }
      }
    }
  }, [availableProjectIds]); // Only re-run if available project IDs change

  // Save selected project to localStorage when user explicitly changes it
  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    updateUserPreferences({
      financeSelectedProjectId: projectId || null,
    });
  };

  useEffect(() => {
    return subscribeUserPreferences((prefs) => {
      if (prefs.financeSelectedProjectId) {
        setSelectedProjectId((prev) => (prev === prefs.financeSelectedProjectId ? prev : prefs.financeSelectedProjectId!));
      }
    });
  }, []);

  // Load transactions when project is selected
  useEffect(() => {
    if (selectedProjectId) {
      loadTransactions();
      loadLiabilities();
      loadPaidLiabilities();
      loadBalance();
      loadProfitData();
    } else {
      setTransactions([]);
      setLiabilities([]);
      setPaidLiabilities([]);
      setBalance(null);
      setAvailableProfit(null);
      setProfitDistributions([]);
    }
  }, [selectedProjectId]);

  const loadTransactions = async () => {
    if (!selectedProjectId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions?projectId=${selectedProjectId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Ensure amount is a number
        const transactions = (data.transactions || []).map((t: Transaction) => ({
          ...t,
          amount: parseFloat(String(t.amount)) || 0
        }));
        setTransactions(transactions);
      } else {
        console.error('Failed to load transactions');
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    if (!selectedProjectId) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions/project/${selectedProjectId}/balance`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      } else {
        console.error('Failed to load balance');
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const loadProfitData = async () => {
    if (!selectedProjectId) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Load available profit
      console.log('[ProfitDistribution] Loading profit data for project:', selectedProjectId);
      const profitResponse = await apiFetch(
        `${API_CONFIG.API_URL}/profit-distribution/project/${selectedProjectId}/available-profit`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      console.log('[ProfitDistribution] Profit response status:', profitResponse.status);
      if (profitResponse.ok) {
        const data = await profitResponse.json();
        console.log('[ProfitDistribution] Profit data:', data);
        setAvailableProfit(data);
      } else {
        console.error('[ProfitDistribution] Failed to load profit:', await profitResponse.text());
      }

      // Load existing distributions
      const distResponse = await apiFetch(
        `${API_CONFIG.API_URL}/profit-distribution/project/${selectedProjectId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (distResponse.ok) {
        const data = await distResponse.json();
        console.log('[ProfitDistribution] Distributions:', data);
        setProfitDistributions(data.distributions || []);
      }
    } catch (error) {
      console.error('[ProfitDistribution] Error loading profit data:', error);
    }
  };

  const loadParticipants = async (preset: string) => {
    if (!selectedProjectId) return;
    
    setIsLoadingParticipants(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/profit-distribution/project/${selectedProjectId}/participants?preset=${preset}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const participants = (data.participants || []).map((p: any) => ({
          ...p,
          percentage: 100 / (data.participants?.length || 1)
        }));
        setProfitParticipants(participants);
      }
    } catch (error) {
      console.error('Error loading participants:', error);
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  const handleCreateDistribution = async () => {
    if (!selectedProjectId || !profitAmount) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/profit-distribution`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            projectId: selectedProjectId,
            name: `Распределение прибыли ${new Date().toLocaleDateString('ru-RU')}`,
            presetType: 'CUSTOM', // Always use CUSTOM since user can edit percentages
            totalAmount: parseFloat(profitAmount),
            shares: profitParticipants.map(p => ({
              recipientType: p.type,
              recipientId: p.id,
              recipientName: p.name,
              sharePercentage: p.percentage
            }))
          })
        }
      );

      if (response.ok) {
        setIsProfitModalOpen(false);
        setProfitAmount('');
        loadProfitData();
        loadBalance();
        loadTransactions();
      }
    } catch (error) {
      console.error('Error creating distribution:', error);
    }
  };

  const handlePayAllShares = async (distributionId: string) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/profit-distribution/${distributionId}/pay-all`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        loadProfitData();
        loadBalance();
        loadTransactions();
      }
    } catch (error) {
      console.error('Error paying shares:', error);
    }
  };

  const handleDeleteDistribution = (distributionId: string, isDistributed: boolean) => {
    setDeleteDistributionModal({ isOpen: true, distributionId, isDistributed });
  };
  
  const confirmDeleteDistribution = async () => {
    const { distributionId } = deleteDistributionModal;
    setDeleteDistributionModal({ isOpen: false, distributionId: '', isDistributed: false });
    
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/profit-distribution/${distributionId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        loadProfitData();
        loadBalance();
        loadTransactions();
      }
    } catch (error) {
      console.error('Error deleting distribution:', error);
    }
  };

  const loadLiabilities = async () => {
    if (!selectedProjectId) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      const url = `${API_CONFIG.API_URL}/transactions/project/${selectedProjectId}/liabilities`;
      const response = await apiFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLiabilities(data.liabilities || []);
      } else {
        console.error('Failed to load liabilities');
      }
    } catch (error) {
      console.error('Error loading liabilities:', error);
    }
  };

  const loadPaidLiabilities = async () => {
    if (!selectedProjectId) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      const url = `${API_CONFIG.API_URL}/transactions/project/${selectedProjectId}/paid-liabilities`;
      const response = await apiFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPaidLiabilities(data.paidLiabilities || []);
      } else {
        console.error('Failed to load paid liabilities');
      }
    } catch (error) {
      console.error('Error loading paid liabilities:', error);
    }
  };

  // Open Revert Payment modal
  const openRevertPaymentModal = (paidLiability: Liability) => {
    setRevertingPayment(paidLiability);
    setIsRevertPaymentModalOpen(true);
  };

  // Confirm Revert Payment
  const handleRevertPaymentConfirm = async () => {
    if (!revertingPayment) return;

    setIsRevertingPayment(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Determine category based on description (producer/coordinator fees vs artist payouts)
      const isManagementFee = revertingPayment.description.includes('Продюсерский гонорар') || 
                              revertingPayment.description.includes('Гонорар координатора') ||
                              revertingPayment.description.includes('Гонорар супервайзера') ||
                              revertingPayment.description.includes('Гонорар за привлечение') ||
                              revertingPayment.description.includes('Producer fee') ||
                              revertingPayment.description.includes('Coordinator fee') ||
                              revertingPayment.description.includes('Supervisor fee') ||
                              revertingPayment.description.includes('Finder fee');
      const category = isManagementFee ? 'OTHER' : 'ARTIST_PAYOUT';
      
      // First, create the LIABILITY transaction (before deleting OUT)
      const createResponse = await apiFetch(
        `${API_CONFIG.API_URL}/transactions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            projectId: revertingPayment.projectId,
            date: new Date().toISOString().split('T')[0],
            amount: -revertingPayment.amount, // Negative for LIABILITY
            type: 'LIABILITY',
            category: category,
            description: revertingPayment.description,
            expenseAssignmentId: revertingPayment.expenseAssignmentId || null
          })
        }
      );

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        console.error('Failed to create LIABILITY:', errorData);
        alert(t('transactions.revertError') || 'Ошибка при создании обязательства');
        return;
      }

      // Then delete the OUT transaction
      const deleteResponse = await apiFetch(
        `${API_CONFIG.API_URL}/transactions/${revertingPayment.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (deleteResponse.ok) {
        setIsRevertPaymentModalOpen(false);
        setRevertingPayment(null);
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      } else {
        // If delete failed, we have a problem - LIABILITY was created but OUT wasn't deleted
        console.error('Failed to delete OUT transaction after creating LIABILITY');
        alert(t('transactions.revertError') || 'Ошибка при удалении выплаты');
        // Reload to show current state
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      }
    } catch (error) {
      console.error('Error reverting payment:', error);
      alert(t('transactions.revertError') || 'Ошибка при отмене оплаты');
    } finally {
      setIsRevertingPayment(false);
    }
  };

  // Open Delete Transaction modal
  const openDeleteTransactionModal = (transactionId: string) => {
    setDeletingTransactionId(transactionId);
    setIsDeleteTransactionModalOpen(true);
  };

  // Confirm Delete Transaction
  const handleDeleteTransactionConfirm = async () => {
    if (!deletingTransactionId) return;

    setIsDeletingTransaction(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions/${deletingTransactionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setIsDeleteTransactionModalOpen(false);
        setDeletingTransactionId(null);
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      } else {
        const error = await response.json();
        alert(error.error || t('transactions.deleteError') || 'Ошибка при удалении');
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(t('transactions.deleteError') || 'Ошибка при удалении');
    } finally {
      setIsDeletingTransaction(false);
    }
  };

  // Alias for backward compatibility
  const handleDeleteTransaction = openDeleteTransactionModal;
  const handleRevertPayment = openRevertPaymentModal;

  const handleCreateTransaction = async () => {
    if (!selectedProjectId || !formAmount || !formDescription.trim()) {
      return;
    }

    // Auto-determine type based on category
    let finalType = formType;
    if (formCategory === 'CLIENT_DEPOSIT') {
      finalType = 'IN';
    } else if (formCategory === 'ARTIST_PAYOUT' || formCategory === 'SOFTWARE_HARDWARE') {
      finalType = 'OUT';
    }

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            projectId: selectedProjectId,
            date: formDate,
            amount: parseFloat(formAmount),
            type: finalType,
            category: formCategory,
            description: formDescription.trim()
          })
        }
      );

      if (response.ok) {
        setIsAddModalOpen(false);
        setFormDate(new Date().toISOString().split('T')[0]);
        setFormAmount('');
        setFormDescription('');
        setFormCategory('CLIENT_DEPOSIT');
        setFormType('IN');
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create transaction');
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      alert('Failed to create transaction');
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editingTransaction || !formAmount || !formDescription.trim()) {
      return;
    }

    // Auto-determine type based on category
    let finalType = formType;
    if (formCategory === 'CLIENT_DEPOSIT') {
      finalType = 'IN';
    } else if (formCategory === 'ARTIST_PAYOUT' || formCategory === 'SOFTWARE_HARDWARE') {
      finalType = 'OUT';
    }

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions/${editingTransaction.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            date: formDate,
            amount: parseFloat(formAmount),
            type: finalType,
            category: formCategory,
            description: formDescription.trim()
          })
        }
      );

      if (response.ok) {
        setIsEditModalOpen(false);
        setEditingTransaction(null);
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update transaction');
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction');
    }
  };


  const handleEditClick = (transaction: Transaction) => {
    // Don't allow editing LIABILITY transactions
    if (transaction.type === 'LIABILITY') {
      return;
    }
    setEditingTransaction(transaction);
    setFormDate(transaction.date);
    setFormAmount(transaction.amount.toString());
    setFormType(transaction.type);
    setFormCategory(transaction.category);
    setFormDescription(transaction.description);
    setIsEditModalOpen(true);
  };

  const handleCategoryChange = (category: Transaction['category']) => {
    setFormCategory(category);
    // Auto-set type based on category
    if (category === 'CLIENT_DEPOSIT') {
      setFormType('IN');
    } else if (category === 'ARTIST_PAYOUT' || category === 'SOFTWARE_HARDWARE') {
      setFormType('OUT');
    }
  };

  const handlePayLiability = (liability: Liability) => {
    setPayingLiability(liability);
    setPayAmount(liability.amount.toString());
    setIsPayModalOpen(true);
  };

  const handlePaySubmit = async () => {
    if (!payingLiability) return;

    // If payAmount is empty, use full amount
    const amount = payAmount ? parseFloat(payAmount) : payingLiability.amount;
    
    if (isNaN(amount) || amount <= 0) {
      alert(t('transactions.invalidAmount') || 'Неверная сумма');
      return;
    }

    if (amount > payingLiability.amount) {
      alert(t('transactions.amountExceedsLiability') || 'Сумма превышает обязательство');
      return;
    }

    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Determine category based on description (producer/coordinator fees vs artist payouts)
      const isManagementFee = payingLiability.description.includes('Продюсерский гонорар') || 
                              payingLiability.description.includes('Гонорар координатора') ||
                              payingLiability.description.includes('Гонорар супервайзера') ||
                              payingLiability.description.includes('Гонорар за привлечение') ||
                              payingLiability.description.includes('Producer fee') ||
                              payingLiability.description.includes('Coordinator fee') ||
                              payingLiability.description.includes('Supervisor fee') ||
                              payingLiability.description.includes('Finder fee');
      const category = isManagementFee ? 'OTHER' : 'ARTIST_PAYOUT';
      
      // Create OUT transaction
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            projectId: payingLiability.projectId,
            date: new Date().toISOString().split('T')[0],
            amount: amount,
            type: 'OUT',
            category: category,
            description: payingLiability.description || `Оплата: ${payingLiability.specialization || 'N/A'}`,
            expenseAssignmentId: payingLiability.expenseAssignmentId
          })
        }
      );

      if (response.ok) {
        // If full payment, delete LIABILITY transaction
        if (amount >= payingLiability.amount) {
          const deleteResponse = await apiFetch(
            `${API_CONFIG.API_URL}/transactions/${payingLiability.id}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          if (!deleteResponse.ok) {
            console.error('Failed to delete liability');
          }
        } else {
          // Partial payment: update LIABILITY amount to remaining
          const remainingAmount = payingLiability.amount - amount;
          const updateResponse = await apiFetch(
            `${API_CONFIG.API_URL}/transactions/${payingLiability.id}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                amount: remainingAmount // Backend will auto-convert to negative for LIABILITY
              })
            }
          );
          if (!updateResponse.ok) {
            console.error('Failed to update liability');
          }
        }

        setIsPayModalOpen(false);
        setPayingLiability(null);
        setPayAmount('');
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      } else {
        const error = await response.json();
        alert(error.error || t('transactions.payError') || 'Ошибка при оплате');
      }
    } catch (error) {
      console.error('Error paying liability:', error);
      alert(t('transactions.payError') || 'Ошибка при оплате');
    }
  };

  // Open Pay All modal
  const openPayAllModal = (pendingItems: Liability[]) => {
    if (pendingItems.length === 0) return;
    setPayAllItems(pendingItems);
    setPayAllArtistName(pendingItems[0]?.artistName || 'артиста');
    setIsPayAllModalOpen(true);
  };

  // Pay all pending liabilities for an artist
  const handlePayAllConfirm = async () => {
    if (payAllItems.length === 0) return;
    
    setIsPayingAll(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      let successCount = 0;
      let errorCount = 0;

      for (const item of payAllItems) {
        try {
          // Determine category
          const isManagementFee = item.description.includes('Продюсерский гонорар') || 
                                  item.description.includes('Гонорар координатора') ||
                                  item.description.includes('Гонорар супервайзера') ||
                                  item.description.includes('Гонорар за привлечение') ||
                                  item.description.includes('Producer fee') ||
                                  item.description.includes('Coordinator fee') ||
                                  item.description.includes('Supervisor fee') ||
                                  item.description.includes('Finder fee');
          const category = isManagementFee ? 'OTHER' : 'ARTIST_PAYOUT';

          // Create OUT transaction
          const response = await apiFetch(
            `${API_CONFIG.API_URL}/transactions`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                projectId: item.projectId,
                date: new Date().toISOString().split('T')[0],
                amount: item.amount,
                type: 'OUT',
                category: category,
                description: item.description,
                expenseAssignmentId: item.expenseAssignmentId
              })
            }
          );

          if (response.ok) {
            // Delete LIABILITY transaction
            await apiFetch(
              `${API_CONFIG.API_URL}/transactions/${item.id}`,
              {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          console.error('Error paying item:', item.id, err);
          errorCount++;
        }
      }

      // Close modal and reset state
      setIsPayAllModalOpen(false);
      setPayAllItems([]);
      setPayAllArtistName('');

      // Reload data
      await loadTransactions();
      await loadLiabilities();
      await loadPaidLiabilities();
      await loadBalance();

      if (errorCount > 0) {
        alert(`${t('transactions.payAllPartialSuccess') || 'Оплачено'}: ${successCount}, ${t('transactions.errors') || 'ошибок'}: ${errorCount}`);
      }
    } catch (error) {
      console.error('Error paying all liabilities:', error);
      alert(t('transactions.payError') || 'Ошибка при оплате');
    } finally {
      setIsPayingAll(false);
    }
  };

  // Open Delete Liability modal
  const openDeleteLiabilityModal = (liability: Liability) => {
    // Only Super Admin can delete
    if (currentRole !== 'Super Admin') {
      alert(t('transactions.deleteNotAllowed') || 'Удаление обязательств доступно только супер-администратору');
      return;
    }
    setDeletingLiability(liability);
    setIsDeleteLiabilityModalOpen(true);
  };

  // Delete a liability (Admin/Producer only)
  const handleDeleteLiabilityConfirm = async () => {
    if (!deletingLiability) return;

    setIsDeletingLiability(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions/${deletingLiability.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setIsDeleteLiabilityModalOpen(false);
        setDeletingLiability(null);
        await loadTransactions();
        await loadLiabilities();
        await loadPaidLiabilities();
        await loadBalance();
      } else {
        const error = await response.json();
        alert(error.error || t('transactions.deleteError') || 'Ошибка при удалении');
      }
    } catch (error) {
      console.error('Error deleting liability:', error);
      alert(t('transactions.deleteError') || 'Ошибка при удалении');
    } finally {
      setIsDeletingLiability(false);
    }
  };

  // Check if user can delete liabilities - only Super Admin
  const canDeleteLiabilities = currentRole === 'Super Admin';

  // Separate producer/coordinator/supervisor fees from artist liabilities
  const { artistLiabilities, feeLiabilities } = useMemo(() => {
    const artist: Liability[] = [];
    const fees: Liability[] = [];
    
    liabilities.forEach(l => {
      // Check if it's a management fee (producer, coordinator, supervisor, or finder) by description
      if (l.description.includes('Продюсерский гонорар') || 
          l.description.includes('Гонорар координатора') ||
          l.description.includes('Гонорар супервайзера') ||
          l.description.includes('Гонорар за привлечение') ||
          l.description.includes('Producer fee') ||
          l.description.includes('Coordinator fee') ||
          l.description.includes('Supervisor fee') ||
          l.description.includes('Finder fee')) {
        fees.push(l);
      } else {
        artist.push(l);
      }
    });
    
    return { artistLiabilities: artist, feeLiabilities: fees };
  }, [liabilities]);
  
  const { paidArtistLiabilities, paidFeeLiabilities } = useMemo(() => {
    const artist: Liability[] = [];
    const fees: Liability[] = [];
    
    paidLiabilities.forEach(l => {
      // Check if it's a management fee (producer, coordinator, supervisor, or finder) by description
      if (l.description.includes('Продюсерский гонорар') || 
          l.description.includes('Гонорар координатора') ||
          l.description.includes('Гонорар супервайзера') ||
          l.description.includes('Гонорар за привлечение') ||
          l.description.includes('Producer fee') ||
          l.description.includes('Coordinator fee') ||
          l.description.includes('Supervisor fee') ||
          l.description.includes('Finder fee')) {
        fees.push(l);
      } else {
        artist.push(l);
      }
    });
    
    return { paidArtistLiabilities: artist, paidFeeLiabilities: fees };
  }, [paidLiabilities]);

  // Group artist liabilities by shot or artist (excluding fees)
  const groupedLiabilities = useMemo(() => {
    if (liabilityGroupBy === 'shot') {
      const grouped: Record<string, Liability[]> = {};
      artistLiabilities.forEach(liability => {
        const key = liability.shotName || liability.shotNumber?.toString() || 'Unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(liability);
      });
      return grouped;
    } else {
      const grouped: Record<string, Liability[]> = {};
      artistLiabilities.forEach(liability => {
        const key = liability.artistName || 'Unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(liability);
      });
      return grouped;
    }
  }, [artistLiabilities, liabilityGroupBy]);

  // Artist summary: combine pending and paid liabilities per artist
  interface ArtistSummary {
    artistId: string | null;
    artistName: string;
    pendingAmount: number;
    paidAmount: number;
    totalAmount: number;
    pendingItems: Liability[];
    paidItems: Liability[];
  }

  const artistSummaries = useMemo((): ArtistSummary[] => {
    const summaryMap: Record<string, ArtistSummary> = {};
    
    // Add pending artist liabilities (excluding fees)
    artistLiabilities.forEach(liability => {
      const key = liability.artistId || liability.artistName || 'unknown';
      if (!summaryMap[key]) {
        summaryMap[key] = {
          artistId: liability.artistId,
          artistName: liability.artistName || 'Не назначен',
          pendingAmount: 0,
          paidAmount: 0,
          totalAmount: 0,
          pendingItems: [],
          paidItems: []
        };
      }
      summaryMap[key].pendingAmount += liability.amount;
      summaryMap[key].totalAmount += liability.amount;
      summaryMap[key].pendingItems.push(liability);
    });
    
    // Add paid artist liabilities (excluding fees)
    paidArtistLiabilities.forEach(liability => {
      const key = liability.artistId || liability.artistName || 'unknown';
      if (!summaryMap[key]) {
        summaryMap[key] = {
          artistId: liability.artistId,
          artistName: liability.artistName || 'Не назначен',
          pendingAmount: 0,
          paidAmount: 0,
          totalAmount: 0,
          pendingItems: [],
          paidItems: []
        };
      }
      summaryMap[key].paidAmount += liability.amount;
      summaryMap[key].totalAmount += liability.amount;
      summaryMap[key].paidItems.push(liability);
    });
    
    return Object.values(summaryMap).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [artistLiabilities, paidArtistLiabilities]);

  const totalLiabilitiesAmount = artistLiabilities.reduce((sum, l) => sum + l.amount, 0);

  // Calculate which liabilities will be covered by a deposit amount
  const calculateCoverage = (depositAmount: number) => {
    if (!depositAmount || depositAmount <= 0 || !balance) {
      return { covered: [], partial: null, remaining: depositAmount || 0 };
    }

    const availableCash = balance.balance;
    const newAvailableCash = availableCash + depositAmount;
    
    // Sort liabilities by creation date (FIFO)
    const sortedLiabilities = [...liabilities].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    const covered: Liability[] = [];
    let remaining = depositAmount;
    let partial: { liability: Liability; amount: number } | null = null;

    for (const liability of sortedLiabilities) {
      if (remaining <= 0) break;
      
      if (remaining >= liability.amount) {
        covered.push(liability);
        remaining -= liability.amount;
      } else {
        partial = { liability, amount: remaining };
        remaining = 0;
        break;
      }
    }

    return { covered, partial, remaining };
  };

  // Filter transactions - exclude LIABILITY type (shown in separate section)
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      // Exclude LIABILITY from transactions table
      if (t.type === 'LIABILITY') return false;
      if (filterType !== 'ALL' && t.type !== filterType) return false;
      if (filterCategory !== 'ALL' && t.category !== filterCategory) return false;
      return true;
    });
  }, [transactions, filterType, filterCategory]);

  // Group similar transactions for display
  interface GroupedTransaction {
    key: string;
    transactions: Transaction[];
    category: Transaction['category'];
    type: 'IN' | 'OUT';
    description: string;
    totalAmount: number;
    count: number;
    latestDate: string;
    isGroup: boolean;
  }

  const groupedTransactions = useMemo((): GroupedTransaction[] => {
    if (!groupSimilarTransactions) {
      // Return as individual items
      return filteredTransactions.map(t => ({
        key: t.id,
        transactions: [t],
        category: t.category,
        type: t.type as 'IN' | 'OUT',
        description: t.description,
        totalAmount: t.amount,
        count: 1,
        latestDate: t.date,
        isGroup: false
      }));
    }

    // Group by category + type + artist name (for artist payouts) or base description
    const groups: Record<string, GroupedTransaction> = {};
    
    filteredTransactions.forEach(t => {
      let groupKey: string;
      let displayDescription: string;
      
      // For artist payouts, group by artist name
      if (t.category === 'ARTIST_PAYOUT' && t.artistName) {
        groupKey = `${t.category}_${t.type}_${t.artistName}`;
        displayDescription = t.artistName;
      } else {
        // For other transactions, group by base description (without shot-specific parts)
        const baseDesc = t.description
          .replace(/EP\d+_[A-Z]+_\d+/g, '') // Remove episode/shot codes
          .replace(/Shot\s*\d+/gi, '') // Remove "Shot 123"
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
        groupKey = `${t.category}_${t.type}_${baseDesc}`;
        displayDescription = baseDesc || t.description;
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          transactions: [],
          category: t.category,
          type: t.type as 'IN' | 'OUT',
          description: displayDescription,
          totalAmount: 0,
          count: 0,
          latestDate: t.date,
          isGroup: false
        };
      }
      
      groups[groupKey].transactions.push(t);
      groups[groupKey].totalAmount += parseFloat(String(t.amount)) || 0;
      groups[groupKey].count++;
      if (t.date > groups[groupKey].latestDate) {
        groups[groupKey].latestDate = t.date;
      }
    });

    // Mark as group if more than 1 transaction
    return Object.values(groups)
      .map(g => ({ ...g, isGroup: g.count > 1 }))
      .sort((a, b) => b.latestDate.localeCompare(a.latestDate));
  }, [filteredTransactions, groupSimilarTransactions]);

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroupExpanded = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Category labels
  const getCategoryLabel = (category: Transaction['category']): string => {
    const labels: Record<Transaction['category'], string> = {
      CLIENT_DEPOSIT: t('transactions.categoryLabels.CLIENT_DEPOSIT') || 'Приход',
      ARTIST_PAYOUT: t('transactions.categoryLabels.ARTIST_PAYOUT') || 'Зарплата',
      SOFTWARE_HARDWARE: t('transactions.categoryLabels.SOFTWARE_HARDWARE') || 'Расходы',
      OTHER: t('transactions.categoryLabels.OTHER') || 'Прочее'
    };
    return labels[category];
  };

  const getCategoryLabelKey = (): string => {
    return t('transactions.category') || 'Категория';
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Format transaction description with artist name if available
  const formatTransactionDescription = (transaction: Transaction): string => {
    let desc = transaction.description;
    // Add artist name in parentheses if available and not already in description
    if (transaction.artistName && !desc.includes(transaction.artistName)) {
      desc = `${desc} (${transaction.artistName})`;
    }
    return desc;
  };

  const currency = selectedProject?.currency || balance?.currency || 'USD';

  return (
    <div className="space-y-6">
      {/* Project Selector + Action Buttons */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('transactions.selectProject') || 'Выбрать проект'}
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => handleProjectChange(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('transactions.selectProject') || 'Выбрать проект'}</option>
              {availableProjects.length === 0 ? (
                <option value="" disabled>{t('transactions.noProjects') || 'Нет доступных проектов'}</option>
              ) : (
                availableProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}{project.approvedBiddingSheetName ? ` / ${project.approvedBiddingSheetName}` : ''}
                  </option>
                ))
              )}
            </select>
          </div>
          
          {/* Action buttons — visible when a project is selected */}
          {selectedProjectId && (
            <div className="flex items-center gap-2 shrink-0">
              {/* Export XLS */}
              {currentRole !== 'Demo' && (
                <button
                  onClick={async () => {
                    try {
                      if (!balance) { alert('No balance data'); return; }
                      const { exportFinanceToExcel } = await import('../utils/excelExport');
                      await exportFinanceToExcel(
                        selectedProject?.name || 'export',
                        transactions,
                        liabilities,
                        paidLiabilities,
                        balance as any
                      );
                    } catch (error) {
                      console.error('Export error:', error);
                      alert(t('transactions.exportError'));
                    }
                  }}
                  className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors flex items-center gap-2"
                  title={t('transactions.exportTooltip')}
                >
                  <ICONS.Download className="w-4 h-4" />
                  <span className="hidden sm:inline">XLS</span>
                </button>
              )}
            </div>
          )}
        </div>
        {availableProjects.length === 0 && (
          <p className="mt-2 text-sm text-yellow-400">
            {t('transactions.noProjectsAvailable') || 'Проекты не загружены. Проверьте подключение к API.'}
          </p>
        )}
      </div>

      {!selectedProjectId && (
        <div className="text-center py-16 text-gray-400">
          <ICONS.CreditCard className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-xl">{t('transactions.selectProjectFirst') || 'Выберите проект для просмотра транзакций'}</p>
        </div>
      )}

      {selectedProjectId && (
        <>
          {/* Two Progress Bars */}
          {balance && (() => {
            const paidExpenses = paidLiabilities.reduce((sum, l) => sum + l.amount, 0);
            const pendingExpenses = Math.abs(balance.pendingLiabilities);
            const paidProfit = balance.paidProfit || 0;
            const totalProfit = balance.totalProfit || 0; // Total expected profit
            const pendingProfit = totalProfit - paidProfit; // Unpaid profit
            
            // Budget = Grand Total from revenue sheet
            const totalBudget = balance.revenueGrandTotal || 0;
            const totalIncome = balance.totalIncome || 0;
            
            // Total to pay = all expenses + all profit = should equal Grand Total
            // Using Grand Total as the denominator ensures bars match
            const totalLiabilities = totalBudget; // Everything needs to be paid out eventually
            
            // Total paid = paid expenses + paid profit
            const totalPaid = paidExpenses + paidProfit;
            
            // Pending = budget - paid
            const totalPending = totalBudget - totalPaid;
            
            if (totalBudget === 0) return null;
            
            // Income progress (how much received vs budget)
            const incomePercent = Math.min((totalIncome / totalBudget) * 100, 100);
            const incomeRemainingPercent = Math.max(0, 100 - incomePercent);
            
            // Payments progress (how much paid vs total liabilities)
            const paymentsPercent = totalLiabilities > 0 ? Math.min((totalPaid / totalLiabilities) * 100, 100) : 0;
            const paymentsRemainingPercent = Math.max(0, 100 - paymentsPercent);
            
            // Calculate prepayment threshold for balance widget
            let prepaymentThreshold = 0;
            if (selectedProject?.prepaymentType && selectedProject?.prepaymentValue && selectedProject?.budget) {
              if (selectedProject.prepaymentType === 'PERCENT') {
                prepaymentThreshold = (selectedProject.budget * selectedProject.prepaymentValue) / 100;
              } else if (selectedProject.prepaymentType === 'FIXED') {
                prepaymentThreshold = selectedProject.prepaymentValue;
              }
            }
            
            const prepaymentRemaining = prepaymentThreshold > 0 ? Math.max(0, prepaymentThreshold - totalIncome) : 0;
            const isPrepaymentComplete = prepaymentThreshold === 0 || totalIncome >= prepaymentThreshold;
            
            // Calculate prepayment marker position
            const prepaymentPercent = totalBudget > 0 && prepaymentThreshold > 0 
              ? Math.min((prepaymentThreshold / totalBudget) * 100, 100) 
              : 0;
            
            return (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Progress Bars Column - takes 2/3, flex to stretch */}
                <div className="lg:col-span-2 flex flex-col gap-4">
                  {/* Income Progress Bar */}
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-md font-semibold text-gray-300">
                        {t('transactions.incomeProgress') || 'Прогресс зачислений'}
                      </h3>
                      <span className="text-sm text-gray-400">
                        {getCurrencySymbol(currency)}{totalIncome.toLocaleString()} / {getCurrencySymbol(currency)}{totalBudget.toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="relative h-6 bg-gray-700 rounded-full overflow-hidden flex-shrink-0">
                      {/* Income fill */}
                      <div 
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500"
                        style={{ width: `${incomePercent}%` }}
                      />
                      {/* Prepayment threshold marker */}
                      {prepaymentPercent > 0 && (
                        <div 
                          className="absolute top-0 h-full w-1 bg-yellow-400 z-10 shadow-lg"
                          style={{ left: `${prepaymentPercent}%`, transform: 'translateX(-50%)' }}
                          title={`${t('transactions.prepaymentLevel') || 'Уровень предоплаты'}: ${getCurrencySymbol(currency)}${prepaymentThreshold.toLocaleString()}`}
                        >
                          {/* Triangle marker on top */}
                          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-b-[6px] border-l-transparent border-r-transparent border-b-yellow-400" />
                        </div>
                      )}
                      {incomePercent > 8 && (
                        <div 
                          className="absolute top-0 h-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ left: 0, width: `${incomePercent}%` }}
                        >
                          {incomePercent.toFixed(0)}%
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm flex-grow">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span className="text-gray-400">{t('transactions.received') || 'Получено'}:</span>
                        <span className="text-blue-400 font-medium">{getCurrencySymbol(currency)}{totalIncome.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gray-600" />
                        <span className="text-gray-400">{t('transactions.awaitingPayment') || 'Ожидает'}:</span>
                        <span className="text-gray-300 font-medium">{getCurrencySymbol(currency)}{(totalBudget - totalIncome).toLocaleString()}</span>
                      </div>
                      {prepaymentThreshold > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-1 bg-yellow-400 rounded" />
                          <span className="text-gray-400">{t('transactions.prepaymentLevel') || 'Предоплата'}:</span>
                          <span className={`font-medium ${isPrepaymentComplete ? 'text-green-400' : 'text-yellow-400'}`}>
                            {getCurrencySymbol(currency)}{prepaymentThreshold.toLocaleString()}
                            {isPrepaymentComplete && ' ✓'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payments Progress Bar */}
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-md font-semibold text-gray-300">
                        {t('transactions.paymentsProgress') || 'Прогресс выплат'}
                      </h3>
                      <span className="text-sm text-gray-400">
                        {getCurrencySymbol(currency)}{totalPaid.toLocaleString()} / {getCurrencySymbol(currency)}{totalLiabilities.toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="relative h-6 bg-gray-700 rounded-full overflow-hidden flex-shrink-0">
                      <div 
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-600 to-green-400 transition-all duration-500"
                        style={{ width: `${paymentsPercent}%` }}
                      />
                      <div 
                        className="absolute top-0 h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all duration-500"
                        style={{ left: `${paymentsPercent}%`, width: `${paymentsRemainingPercent}%` }}
                      />
                      {paymentsPercent > 8 && (
                        <div 
                          className="absolute top-0 h-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ left: 0, width: `${paymentsPercent}%` }}
                        >
                          {paymentsPercent.toFixed(0)}%
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm flex-grow">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-gray-400">{t('transactions.paid') || 'Выплачено'}:</span>
                        <span className="text-green-400 font-medium">{getCurrencySymbol(currency)}{totalPaid.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span className="text-gray-400">{t('transactions.pending') || 'Ожидает'}:</span>
                        <span className="text-orange-400 font-medium">{getCurrencySymbol(currency)}{totalPending.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Widget Column - takes 1/3, flex to match height */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex flex-col">
                  <h2 className="text-md font-semibold text-gray-300 mb-3">
                    {t('transactions.balance') || 'Баланс'}
                  </h2>
                  <div className="space-y-3 flex-grow">
                    {/* Gross Income - BLUE (matches income progress bar) */}
                    <div className="flex items-center justify-between p-2 bg-gray-900/30 rounded border-l-4 border-blue-500">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span className="text-sm text-gray-400">{t('transactions.totalGrossIncome') || 'Получено'}</span>
                      </div>
                      <span className="font-bold text-blue-400">{getCurrencySymbol(currency)}{balance.totalIncome.toLocaleString()}</span>
                    </div>

                    {/* Tax Reserve - YELLOW */}
                    <div className="flex items-center justify-between p-2 bg-gray-900/30 rounded border-l-4 border-yellow-500">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span className="text-sm text-gray-400">{t('transactions.taxReserve') || 'Налоговый резерв'}</span>
                      </div>
                      <span className="font-bold text-yellow-400">{getCurrencySymbol(currency)}{balance.taxReserve.toLocaleString()}</span>
                    </div>

                    {/* Pending Liabilities - ORANGE (matches pending in payments progress) */}
                    <div className="flex items-center justify-between p-2 bg-gray-900/30 rounded border-l-4 border-orange-500">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span className="text-sm text-gray-400">{t('transactions.pendingLiabilities') || 'Ожидающие обязательства'}</span>
                      </div>
                      <span className="font-bold text-orange-400">{getCurrencySymbol(currency)}{Math.abs(balance.pendingLiabilities).toLocaleString()}</span>
                    </div>

                    {/* Net Cash Available - GREEN/RED based on value */}
                    <div className={`flex items-center justify-between p-2 bg-gray-900/30 rounded border-l-4 ${balance.balance >= 0 ? 'border-green-500' : 'border-red-500'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${balance.balance >= 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-sm text-gray-400">{t('transactions.netCashAvailable') || 'Доступные средства'}</span>
                      </div>
                      <span className={`font-bold ${balance.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {getCurrencySymbol(currency)}{balance.balance.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  {/* Warnings - at bottom */}
                  <div className="mt-auto pt-3">
                    {prepaymentThreshold > 0 && !isPrepaymentComplete && (
                      <div className="bg-orange-900/20 border border-orange-500/50 rounded p-2 text-xs mb-2">
                        <div className="font-bold text-orange-400">⚠️ Требуется предоплата</div>
                        <div className="text-orange-300 mt-1">
                          Осталось: {getCurrencySymbol(currency)}{prepaymentRemaining.toLocaleString()}
                        </div>
                      </div>
                    )}
                    
                    {balance.pendingLiabilities > balance.balance && (
                      <div className="bg-red-900/20 border border-red-500/50 rounded p-2 text-xs">
                        <div className="font-bold text-red-400">⚠️ Кассовый разрыв</div>
                        <div className="text-red-300 mt-1">
                          Нехватка: {getCurrencySymbol(currency)}{(balance.pendingLiabilities - balance.balance).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Two-column layout: Transactions on left, Payments on right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT COLUMN: Transactions */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              {/* Collapsible header */}
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsTransactionsExpanded(!isTransactionsExpanded)}
              >
                <div className="flex items-center gap-2">
                  <ICONS.ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isTransactionsExpanded ? 'rotate-90' : ''}`} />
                  <h3 className="text-md font-semibold text-gray-300">
                    {t('transactions.title') || 'Транзакции'}
                  </h3>
                  <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                    {filteredTransactions.length}
                  </span>
                </div>
                {isTransactionsExpanded && (
                  <div className="flex items-center gap-2">
                    {/* Add button */}
                    {canEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsAddModalOpen(true); }}
                        className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded transition-colors flex items-center gap-2"
                      >
                        <ICONS.Plus className="w-4 h-4" />
                        {t('transactions.addTransaction')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {isTransactionsExpanded && (
                <>
                  {/* Filters + Group toggle */}
                  <div className="flex items-center justify-between gap-2 mt-3 mb-3">
                    <div className="flex gap-2">
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as 'ALL' | 'IN' | 'OUT')}
                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="ALL">{t('transactions.filter.all') || 'Все'}</option>
                        <option value="IN">{t('transactions.in') || 'Приход'}</option>
                        <option value="OUT">{t('transactions.out') || 'Расход'}</option>
                      </select>

                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value as 'ALL' | Transaction['category'])}
                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="ALL">{t('transactions.filter.allCategories') || 'Все категории'}</option>
                        <option value="CLIENT_DEPOSIT">{getCategoryLabel('CLIENT_DEPOSIT')}</option>
                        <option value="ARTIST_PAYOUT">{getCategoryLabel('ARTIST_PAYOUT')}</option>
                        <option value="SOFTWARE_HARDWARE">{getCategoryLabel('SOFTWARE_HARDWARE')}</option>
                        <option value="OTHER">{getCategoryLabel('OTHER')}</option>
                      </select>
                    </div>

                    {/* Group similar toggle */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-gray-400">{t('transactions.groupSimilar') || 'Группировать'}</span>
                      <div 
                        onClick={() => setGroupSimilarTransactions(!groupSimilarTransactions)}
                        className={`relative w-8 h-4 rounded-full transition-colors ${groupSimilarTransactions ? 'bg-indigo-600' : 'bg-gray-600'}`}
                      >
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${groupSimilarTransactions ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                    </label>
                  </div>

            {loading ? (
              <div className="text-center py-4 text-gray-400">
                {t('common.loading') || 'Загрузка...'}
              </div>
            ) : groupedTransactions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p>{t('transactions.noTransactions') || 'Нет транзакций'}</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-700">
                <table className="w-full">
                  <thead className="bg-gray-700/50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">
                        {t('transactions.date') || 'Дата'}
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">
                        {getCategoryLabelKey()}
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">
                        {t('transactions.description') || 'Описание'}
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-300">
                        {t('transactions.amount') || 'Сумма'}
                      </th>
                      {canEdit && (
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-300 w-16">
                          {t('common.actions') || ''}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {groupedTransactions.map(group => (
                      <React.Fragment key={group.key}>
                        {/* Group header row */}
                        <tr 
                          className={`hover:bg-gray-700/30 ${group.isGroup ? 'cursor-pointer' : ''}`}
                          onClick={() => group.isGroup && toggleGroupExpanded(group.key)}
                        >
                          <td className="px-3 py-2 text-xs text-gray-300">
                            <div className="flex items-center gap-1">
                              {group.isGroup && (
                                <ICONS.ChevronRight className={`w-3 h-3 text-gray-500 transition-transform ${expandedGroups.has(group.key) ? 'rotate-90' : ''}`} />
                              )}
                              {formatDate(group.latestDate)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-300">
                            {getCategoryLabel(group.category)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-300">
                            <div className="flex items-center gap-2">
                              {group.isGroup ? group.description : formatTransactionDescription(group.transactions[0])}
                              {group.isGroup && (
                                <span className="px-1.5 py-0.5 bg-gray-600 rounded text-[10px] text-gray-300">
                                  ×{group.count}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={`px-3 py-2 text-xs text-right font-medium ${
                            group.type === 'IN' ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {group.type === 'IN' ? '+' : '-'}
                            {getCurrencySymbol(currency)}{group.totalAmount.toLocaleString()}
                          </td>
                          {canEdit && (
                            <td className="px-3 py-2 text-right">
                              {!group.isGroup && (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEditClick(group.transactions[0]); }}
                                    className="text-indigo-400 hover:text-indigo-300"
                                    title={t('common.edit') || 'Редактировать'}
                                  >
                                    <ICONS.Edit className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteTransaction(group.transactions[0].id); }}
                                    className="text-red-400 hover:text-red-300"
                                    title={t('common.delete') || 'Удалить'}
                                  >
                                    <ICONS.Trash className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                        {/* Expanded group items */}
                        {group.isGroup && expandedGroups.has(group.key) && group.transactions.map(transaction => (
                          <tr key={transaction.id} className="bg-gray-800/30 hover:bg-gray-700/30">
                            <td className="px-3 py-1.5 text-xs text-gray-400 pl-8">
                              {formatDate(transaction.date)}
                            </td>
                            <td className="px-3 py-1.5 text-xs text-gray-400">
                              {getCategoryLabel(transaction.category)}
                            </td>
                            <td className="px-3 py-1.5 text-xs text-gray-400">
                              {formatTransactionDescription(transaction)}
                            </td>
                            <td className={`px-3 py-1.5 text-xs text-right font-medium ${
                              transaction.type === 'IN' ? 'text-green-400/70' : 'text-red-400/70'
                            }`}>
                              {transaction.type === 'IN' ? '+' : '-'}
                              {getCurrencySymbol(currency)}{transaction.amount.toLocaleString()}
                            </td>
                            {canEdit && (
                              <td className="px-3 py-1.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => handleEditClick(transaction)}
                                    className="text-indigo-400 hover:text-indigo-300"
                                    title={t('common.edit') || 'Редактировать'}
                                  >
                                    <ICONS.Edit className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTransaction(transaction.id)}
                                    className="text-red-400 hover:text-red-300"
                                    title={t('common.delete') || 'Удалить'}
                                  >
                                    <ICONS.Trash className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
                </>
              )}
            </div>

            {/* RIGHT COLUMN: Management Fees + Artist Payments */}
            <div className="space-y-4">
              {/* Producer/Coordinator Fees Section */}
              {(feeLiabilities.length > 0 || paidFeeLiabilities.length > 0) && (
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  {/* Collapsible header */}
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setIsManagementFeesExpanded(!isManagementFeesExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <ICONS.ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isManagementFeesExpanded ? 'rotate-90' : ''}`} />
                      <h3 className="text-md font-semibold text-gray-300">
                        {t('transactions.managementFees') || 'Гонорары управления'}
                      </h3>
                      <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                        {feeLiabilities.length + paidFeeLiabilities.length}
                      </span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {paidFeeLiabilities.length > 0 && (
                        <span className="text-green-400">✓ {getCurrencySymbol(currency)}{paidFeeLiabilities.reduce((s, f) => s + f.amount, 0).toLocaleString()}</span>
                      )}
                      {feeLiabilities.length > 0 && (
                        <span className="text-orange-400">⏳ {getCurrencySymbol(currency)}{feeLiabilities.reduce((s, f) => s + f.amount, 0).toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  {isManagementFeesExpanded && (
                    <div className="space-y-2 mt-3">
                      {/* Pending fees */}
                      {feeLiabilities.map(fee => {
                  const isProducer = fee.description.includes('Продюсер') || fee.description.includes('Producer');
                  const isSupervisor = fee.description.includes('супервайзер') || fee.description.includes('Supervisor');
                  const isFinder = fee.description.includes('привлечение') || fee.description.includes('Finder');
                  const personName = isProducer 
                    ? (balance?.producerName || t('transactions.producer') || 'Продюсер')
                    : isSupervisor
                    ? (balance?.supervisorName || t('transactions.supervisor') || 'Супервайзер')
                    : isFinder
                    ? (balance?.finderName || t('transactions.finder') || 'Finder')
                    : (balance?.coordinatorName || t('transactions.coordinator') || 'Координатор');
                  
                  return (
                    <div key={fee.id} className="flex items-center justify-between p-3 bg-gray-900/30 rounded-lg border border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-sm font-bold">
                          {personName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">{personName}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-2">
                            <span>
                              {isProducer 
                                ? (t('transactions.producerFee') || 'Продюсерский гонорар')
                                : isSupervisor
                                ? (t('transactions.supervisorFee') || 'Гонорар супервайзера')
                                : isFinder
                                ? (t('transactions.finderFee') || 'Гонорар за привлечение')
                                : (t('transactions.coordinatorFee') || 'Гонорар координатора')
                              }
                            </span>
                            <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-[10px] font-medium">
                              {t('transactions.remaining') || 'остаток'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{formatDate(fee.date)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-orange-400 font-bold">{getCurrencySymbol(currency)}{fee.amount.toLocaleString()}</span>
                        {canEdit && (
                          <button
                            onClick={() => handlePayLiability(fee)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                          >
                            {t('transactions.pay') || 'Оплатить'}
                          </button>
                        )}
                        {/* No delete button for management fees - they are managed via bidding sheet settings */}
                      </div>
                    </div>
                  );
                      })}
                      {/* Paid fees */}
                      {paidFeeLiabilities.map(fee => {
                  const isProducer = fee.description.includes('Продюсер') || fee.description.includes('Producer');
                  const isSupervisor = fee.description.includes('супервайзер') || fee.description.includes('Supervisor');
                  const isFinder = fee.description.includes('привлечение') || fee.description.includes('Finder');
                  const personName = isProducer 
                    ? (balance?.producerName || t('transactions.producer') || 'Продюсер')
                    : isSupervisor
                    ? (balance?.supervisorName || t('transactions.supervisor') || 'Супервайзер')
                    : isFinder
                    ? (balance?.finderName || t('transactions.finder') || 'Finder')
                    : (balance?.coordinatorName || t('transactions.coordinator') || 'Координатор');
                  
                  return (
                    <div key={fee.id} className="flex items-center justify-between p-3 bg-green-900/10 rounded-lg border border-green-900/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-sm font-bold">
                          ✓
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-300">{personName}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-2">
                            <span>
                              {isProducer 
                                ? (t('transactions.producerFee') || 'Продюсерский гонорар')
                                : isSupervisor
                                ? (t('transactions.supervisorFee') || 'Гонорар супервайзера')
                                : isFinder
                                ? (t('transactions.finderFee') || 'Гонорар за привлечение')
                                : (t('transactions.coordinatorFee') || 'Гонорар координатора')
                              }
                            </span>
                            <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-[10px] font-medium">
                              {t('transactions.paid') || 'оплачено'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{formatDate(fee.date)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-green-400 font-bold">{getCurrencySymbol(currency)}{fee.amount.toLocaleString()}</span>
                        {canEdit && (
                          <button
                            onClick={() => handleRevertPayment(fee)}
                            className="px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded transition-colors"
                          >
                            {t('transactions.revert') || 'Отменить выплату'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Artist Liabilities Section */}
              {(artistLiabilities.length > 0 || paidArtistLiabilities.length > 0) && (
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  {/* Collapsible header */}
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setIsArtistPaymentsExpanded(!isArtistPaymentsExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <ICONS.ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isArtistPaymentsExpanded ? 'rotate-90' : ''}`} />
                      <h3 className="text-md font-semibold text-gray-300">
                        {t('transactions.artistPayments') || 'Выплаты артистам'}
                      </h3>
                      <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                        {artistLiabilities.length + paidArtistLiabilities.length}
                      </span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {paidArtistLiabilities.length > 0 && (
                        <span className="text-green-400">✓ {getCurrencySymbol(currency)}{paidArtistLiabilities.reduce((s, l) => s + l.amount, 0).toLocaleString()}</span>
                      )}
                      {artistLiabilities.length > 0 && (
                        <span className="text-orange-400">⏳ {getCurrencySymbol(currency)}{artistLiabilities.reduce((s, l) => s + l.amount, 0).toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  {isArtistPaymentsExpanded && (
                    <>
                      {/* Toggle between pending and paid + grouping */}
                      <div className="flex items-center justify-between mt-3 mb-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setLiabilityView('pending'); }}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              liabilityView === 'pending'
                                ? 'bg-orange-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {t('transactions.pending') || 'Ожидает'} ({artistLiabilities.length})
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setLiabilityView('paid'); }}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              liabilityView === 'paid'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {t('transactions.paid') || 'Выплачено'} ({paidArtistLiabilities.length})
                          </button>
                        </div>
                        <select
                          value={liabilityGroupBy}
                          onChange={(e) => { e.stopPropagation(); setLiabilityGroupBy(e.target.value as 'shot' | 'artist' | 'artist-summary'); }}
                          className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="shot">{t('transactions.groupByShot') || 'По шоту'}</option>
                          <option value="artist">{t('transactions.groupByArtist') || 'По артисту'}</option>
                          <option value="artist-summary">{t('transactions.groupByArtistSummary') || 'Сводка'}</option>
                        </select>
                      </div>

                      {/* Artist Summary View */}
              {liabilityGroupBy === 'artist-summary' ? (
                <div className="space-y-3">
                  {artistSummaries.length > 0 ? (
                    artistSummaries.map(summary => {
                      const paidPercent = summary.totalAmount > 0 
                        ? (summary.paidAmount / summary.totalAmount) * 100 
                        : 0;
                      
                      return (
                        <div key={summary.artistId || summary.artistName} className="bg-gray-900/30 rounded-lg p-4 border border-gray-700">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                                {summary.artistName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="font-semibold text-white text-lg">{summary.artistName}</span>
                                <div className="text-xs text-gray-400">
                                  {summary.pendingItems.length + summary.paidItems.length} {t('transactions.tasks') || 'задач'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-sm text-gray-400">
                                  {t('transactions.paidOf') || 'Оплачено:'} <span className="text-green-400 font-medium">{getCurrencySymbol(currency)}{summary.paidAmount.toLocaleString()}</span>
                                  {' '}{t('transactions.of') || 'из'}{' '}
                                  <span className="text-white font-bold">{getCurrencySymbol(currency)}{summary.totalAmount.toLocaleString()}</span>
                                </div>
                              </div>
                              {/* Pay All button */}
                              {canEdit && summary.pendingItems.length > 0 && (
                                <button
                                  onClick={() => openPayAllModal(summary.pendingItems)}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1"
                                  title={t('transactions.payAll') || 'Выплатить всё'}
                                >
                                  <ICONS.Check className="w-4 h-4" />
                                  {t('transactions.payAll') || 'Выплатить всё'}
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Progress bar */}
                          <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden mb-3">
                            <div 
                              className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-600 to-green-400 transition-all duration-500"
                              style={{ width: `${paidPercent}%` }}
                            />
                          </div>
                          
                          {/* Task details - collapsible */}
                          <details className="group">
                            <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300 flex items-center gap-2">
                              <ICONS.ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                              {t('transactions.showDetails') || 'Показать детали'}
                            </summary>
                            <div className="mt-3 ml-6 space-y-2">
                              {/* Pending items */}
                              {summary.pendingItems.length > 0 && (
                                <div className="mb-2">
                                  <div className="text-xs font-medium text-orange-400 mb-1">
                                    {t('transactions.pendingTasks') || 'Ожидает оплаты'} ({summary.pendingItems.length})
                                  </div>
                                  {summary.pendingItems.map(item => (
                                    <div key={item.id} className="flex items-center justify-between py-1 px-2 hover:bg-gray-800/50 rounded text-sm">
                                      <span className="text-gray-300">
                                        {item.shotName || `Shot ${item.shotNumber}`} — {item.specialization}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-orange-400">{getCurrencySymbol(currency)}{item.amount.toLocaleString()}</span>
                                        {canEdit && (
                                          <button
                                            onClick={() => handlePayLiability(item)}
                                            className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                                          >
                                            {t('transactions.pay') || 'Оплатить'}
                                          </button>
                                        )}
                                        {canDeleteLiabilities && (
                                          <button
                                            onClick={() => openDeleteLiabilityModal(item)}
                                            className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                                            title={t('transactions.deleteLiability') || 'Удалить обязательство'}
                                          >
                                            <ICONS.Trash className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Paid items */}
                              {summary.paidItems.length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-green-400 mb-1">
                                    {t('transactions.paidTasks') || 'Оплачено'} ({summary.paidItems.length})
                                  </div>
                                  {summary.paidItems.map(item => (
                                    <div key={item.id} className="flex items-center justify-between py-1 px-2 hover:bg-gray-800/50 rounded text-sm">
                                      <span className="text-gray-400">
                                        {item.shotName || `Shot ${item.shotNumber}`} — {item.specialization}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-green-400">{getCurrencySymbol(currency)}{item.amount.toLocaleString()}</span>
                                        {canEdit && (
                                          <button
                                            onClick={() => handleRevertPayment(item)}
                                            className="px-2 py-0.5 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded"
                                          >
                                            {t('transactions.revert') || 'Вернуть'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </details>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      {t('transactions.noLiabilities') || 'Нет обязательств'}
                    </div>
                  )}
                  
                  {/* Grand totals */}
                  {artistSummaries.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{t('transactions.totalPaid') || 'ВСЕГО ВЫПЛАЧЕНО:'}</span>
                        <span className="text-green-400 font-bold">
                          {getCurrencySymbol(currency)}{artistSummaries.reduce((sum, s) => sum + s.paidAmount, 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-400">{t('transactions.totalPending') || 'ВСЕГО ОЖИДАЕТ:'}</span>
                        <span className="text-orange-400 font-bold">
                          {getCurrencySymbol(currency)}{artistSummaries.reduce((sum, s) => sum + s.pendingAmount, 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-lg mt-2">
                        <span className="text-gray-300 font-semibold">{t('transactions.grandTotal') || 'ИТОГО:'}</span>
                        <span className="text-white font-bold">
                          {getCurrencySymbol(currency)}{artistSummaries.reduce((sum, s) => sum + s.totalAmount, 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : liabilityView === 'pending' ? (
                // Pending artist liabilities
                artistLiabilities.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {Object.entries(groupedLiabilities).map(([groupKey, groupLiabilities]) => {
                        const groupTotal = groupLiabilities.reduce((sum, l) => sum + l.amount, 0);
                        return (
                          <div key={groupKey} className="bg-gray-900/30 rounded-lg p-3 border border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <ICONS.ChevronDown className="w-4 h-4 text-gray-400" />
                                <span className="font-semibold text-white">
                                  {groupKey} ({groupLiabilities.length})
                                </span>
                              </div>
                              <span className="text-sm font-bold text-red-400">
                                {getCurrencySymbol(currency)}{groupTotal.toLocaleString()}
                              </span>
                            </div>
                            <div className="ml-6 space-y-1">
                              {groupLiabilities.map(liability => (
                                <div key={liability.id} className="flex items-center justify-between py-1 px-2 hover:bg-gray-800/50 rounded">
                                  <div className="flex-1">
                                    <div className="text-sm text-gray-300">
                                      {liability.specialization || 'N/A'}
                                      {liability.artistName && ` — ${liability.artistName}`}
                                      {liability.shotName && liabilityGroupBy === 'artist' && ` (${liability.shotName})`}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {liability.description}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-white">
                                      {getCurrencySymbol(currency)}{liability.amount.toLocaleString()}
                                    </span>
                                    {canEdit && (
                                      <button
                                        onClick={() => handlePayLiability(liability)}
                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                      >
                                        {t('transactions.pay') || 'Оплатить'}
                                      </button>
                                    )}
                                    {canDeleteLiabilities && (
                                      <button
                                        onClick={() => openDeleteLiabilityModal(liability)}
                                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                                        title={t('transactions.deleteLiability') || 'Удалить обязательство'}
                                      >
                                        <ICONS.Trash className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-300">
                        {t('transactions.total') || 'ИТОГО:'}
                      </span>
                      <span className="text-lg font-bold text-red-400">
                        {getCurrencySymbol(currency)}{totalLiabilitiesAmount.toLocaleString()}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    {t('transactions.noPendingLiabilities') || 'Нет ожидающих обязательств'}
                  </div>
                )
              ) : (
                // Paid artist liabilities
                paidArtistLiabilities.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {(() => {
                        // Group paid artist liabilities
                        const grouped: Record<string, Liability[]> = {};
                        paidArtistLiabilities.forEach(liability => {
                          const key = liabilityGroupBy === 'shot' 
                            ? (liability.shotName || liability.shotNumber?.toString() || 'Unknown')
                            : (liability.artistName || 'Unknown');
                          if (!grouped[key]) grouped[key] = [];
                          grouped[key].push(liability);
                        });
                        
                        return Object.entries(grouped).map(([groupKey, groupLiabilities]) => {
                          const groupTotal = groupLiabilities.reduce((sum, l) => sum + l.amount, 0);
                          return (
                            <div key={groupKey} className="bg-gray-900/30 rounded-lg p-3 border border-green-900/30">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <ICONS.Check className="w-4 h-4 text-green-400" />
                                  <span className="font-semibold text-white">
                                    {groupKey} ({groupLiabilities.length})
                                  </span>
                                </div>
                                <span className="text-sm font-bold text-green-400">
                                  {getCurrencySymbol(currency)}{groupTotal.toLocaleString()}
                                </span>
                              </div>
                              <div className="ml-6 space-y-1">
                                {groupLiabilities.map(liability => (
                                  <div key={liability.id} className="flex items-center justify-between py-1 px-2 hover:bg-gray-800/50 rounded">
                                    <div className="flex-1">
                                      <div className="text-sm text-gray-300">
                                        {liability.specialization || 'N/A'}
                                        {liability.artistName && ` — ${liability.artistName}`}
                                        {liability.shotName && liabilityGroupBy === 'artist' && ` (${liability.shotName})`}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {liability.description}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-medium text-white">
                                        {getCurrencySymbol(currency)}{liability.amount.toLocaleString()}
                                      </span>
                                      {canEdit && (
                                        <button
                                          onClick={() => handleRevertPayment(liability)}
                                          className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded transition-colors"
                                          title={t('transactions.revertPayment') || 'Отменить оплату'}
                                        >
                                          {t('transactions.revert') || 'Вернуть'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-300">
                        {t('transactions.totalPaid') || 'ВСЕГО ВЫПЛАЧЕНО:'}
                      </span>
                      <span className="text-lg font-bold text-green-400">
                        {getCurrencySymbol(currency)}{paidArtistLiabilities.reduce((sum, l) => sum + l.amount, 0).toLocaleString()}
                      </span>
                    </div>
                  </>
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          {t('transactions.noPaidLiabilities') || 'Нет выплаченных обязательств'}
                        </div>
                      )
                    )}
                    </>
                  )}
                </div>
              )}
            </div>
            {/* End of RIGHT COLUMN */}
          </div>
          {/* End of grid */}

          {/* Profit Distribution Section */}
          {selectedProjectId && (
            <div className="mt-6 bg-gray-800/50 border border-purple-700/50 rounded-lg overflow-hidden">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/70"
                onClick={() => setIsProfitSectionExpanded(!isProfitSectionExpanded)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                    <ICONS.CreditCard className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-md font-semibold text-purple-300">
                    {t('profitDistribution.title') || 'Распределение прибыли'}
                  </h3>
                  {availableProfit && (
                    <span className="text-xs bg-purple-900/50 text-purple-300 px-2 py-0.5 rounded">
                      {t('profitDistribution.available') || 'Доступно'}: {getCurrencySymbol(currency)}{availableProfit.availableProfit.toLocaleString()}
                    </span>
                  )}
                </div>
                <ICONS.ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isProfitSectionExpanded ? 'rotate-180' : ''}`} />
              </div>

              {isProfitSectionExpanded && (
                <div className="p-4 pt-0 space-y-4">
                  {/* Profit Summary */}
                  {availableProfit && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-2 p-3 bg-gray-900/30 rounded-lg items-center">
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.revenueTotal') || 'Grand Total'}</div>
                        <div className="text-sm font-medium text-blue-400">{getCurrencySymbol(currency)}{availableProfit.revenueGrandTotal.toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.taxReserve') || 'Налог'}</div>
                        <div className="text-sm font-medium text-yellow-400">-{getCurrencySymbol(currency)}{availableProfit.taxReserve.toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.expenses') || 'Расходы'}</div>
                        <div className="text-sm font-medium text-red-400">-{getCurrencySymbol(currency)}{availableProfit.expenseTotal.toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.producerFee') || 'Продюсер'}</div>
                        <div className="text-sm font-medium text-orange-400">-{getCurrencySymbol(currency)}{availableProfit.producerFee.toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.coordinatorFee') || 'Координатор'}</div>
                        <div className="text-sm font-medium text-orange-400">-{getCurrencySymbol(currency)}{availableProfit.coordinatorFee.toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.supervisorFee') || 'Супервайзер'}</div>
                        <div className="text-sm font-medium text-orange-400">-{getCurrencySymbol(currency)}{(availableProfit.supervisorFee || 0).toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.finderFee') || 'Finder'}</div>
                        <div className="text-sm font-medium text-orange-400">-{getCurrencySymbol(currency)}{(availableProfit.finderFee || 0).toLocaleString()}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">{t('profitDistribution.distributed') || 'Распределено'}</div>
                        <div className="text-sm font-medium text-purple-400">-{getCurrencySymbol(currency)}{availableProfit.alreadyDistributed.toLocaleString()}</div>
                      </div>
                      <div className="text-center border-l border-gray-700 pl-2">
                        <div className="text-xs text-gray-500">{t('profitDistribution.availableProfit') || 'Прибыль'}</div>
                        <div className={`text-sm font-bold ${availableProfit.availableProfit > 0 ? 'text-green-400' : 'text-gray-400'}`}>
                          {getCurrencySymbol(currency)}{availableProfit.availableProfit.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Create Distribution Button */}
                  {canEdit && availableProfit && availableProfit.availableProfit > 0 && (
                    <button
                      onClick={() => {
                        setIsProfitModalOpen(true);
                        setProfitAmount(availableProfit.availableProfit.toString());
                        loadParticipants(profitPreset);
                      }}
                      className="w-full py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <ICONS.Plus className="w-4 h-4" />
                      {t('profitDistribution.createDistribution') || 'Создать распределение'}
                    </button>
                  )}

                  {/* Existing Distributions */}
                  {profitDistributions.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-400">{t('profitDistribution.distributions') || 'Распределения'}</h4>
                      {profitDistributions.map(dist => (
                        <div key={dist.id} className="p-3 bg-gray-900/30 rounded-lg border border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{dist.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                dist.status === 'distributed' ? 'bg-green-900/50 text-green-400' :
                                dist.status === 'approved' ? 'bg-blue-900/50 text-blue-400' :
                                'bg-gray-700 text-gray-300'
                              }`}>
                                {dist.status === 'distributed' ? (t('profitDistribution.paid') || 'Выплачено') :
                                 dist.status === 'approved' ? (t('profitDistribution.approved') || 'Утверждено') :
                                 (t('profitDistribution.draft') || 'Черновик')}
                              </span>
                            </div>
                            <span className="font-bold text-purple-400">{getCurrencySymbol(currency)}{dist.totalAmount.toLocaleString()}</span>
                          </div>
                          
                          {/* Shares */}
                          <div className="space-y-1">
                            {(dist.shares || []).map((share: any) => (
                              <div key={share.id} className="flex items-center justify-between text-sm py-1 px-2 bg-gray-800/50 rounded">
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    share.recipientType === 'user' ? 'bg-blue-600' : 'bg-orange-600'
                                  } text-white`}>
                                    {share.recipientName.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-gray-300">{share.recipientName}</span>
                                  <span className="text-xs text-gray-500">({share.sharePercentage.toFixed(1)}%)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={share.isPaid ? 'text-green-400' : 'text-gray-300'}>
                                    {getCurrencySymbol(currency)}{share.shareAmount.toLocaleString()}
                                  </span>
                                  {share.isPaid && <ICONS.Check className="w-4 h-4 text-green-400" />}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 mt-2">
                            {dist.status !== 'distributed' && (dist.shares || []).some((s: any) => !s.isPaid) && (
                              <button
                                onClick={() => handlePayAllShares(dist.id)}
                                className="flex-1 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                              >
                                {t('profitDistribution.payAll') || 'Выплатить всем'}
                              </button>
                            )}
                            {canEdit && (
                              <button
                                onClick={() => handleDeleteDistribution(dist.id, dist.status === 'distributed')}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors flex items-center gap-1"
                                title={t('profitDistribution.deleteDistribution') || 'Удалить распределение'}
                              >
                                <ICONS.Trash className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty State */}
                  {profitDistributions.length === 0 && (!availableProfit || availableProfit.availableProfit <= 0) && (
                    <div className="text-center py-6 text-gray-400">
                      <ICONS.CreditCard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('profitDistribution.noProfitYet') || 'Прибыль пока недоступна для распределения'}</p>
                      <p className="text-xs mt-1">{t('profitDistribution.profitHint') || 'Прибыль появится после получения оплаты и выплаты всех обязательств'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </>
      )}

      {/* Profit Distribution Modal */}
      {isProfitModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <ICONS.CreditCard className="w-5 h-5 text-purple-400" />
              {t('profitDistribution.createDistribution') || 'Создать распределение прибыли'}
            </h2>

            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('profitDistribution.amount') || 'Сумма'}
                </label>
                <input
                  type="number"
                  value={profitAmount}
                  onChange={(e) => setProfitAmount(e.target.value)}
                  max={availableProfit?.availableProfit || 0}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('profitDistribution.maxAvailable') || 'Максимум'}: {getCurrencySymbol(currency)}{availableProfit?.availableProfit.toLocaleString()}
                </p>
              </div>

              {/* Preset */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('profitDistribution.distributionPreset') || 'Пресет распределения'}
                </label>
                <select
                  value={profitPreset}
                  onChange={(e) => {
                    const preset = e.target.value as typeof profitPreset;
                    setProfitPreset(preset);
                    loadParticipants(preset);
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                >
                  <option value="EQUAL_MANAGERS">{t('profitDistribution.equalManagers') || 'Равные доли среди управляющих'}</option>
                  <option value="EQUAL_ARTISTS">{t('profitDistribution.equalArtists') || 'Равные доли среди исполнителей'}</option>
                  <option value="EQUAL_ALL">{t('profitDistribution.equalAll') || 'Равные доли среди всех'}</option>
                  <option value="CUSTOM">{t('profitDistribution.customDistribution') || 'Произвольное распределение'}</option>
                </select>
              </div>

              {/* Participants Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('profitDistribution.participants') || 'Участники'} ({profitParticipants.length})
                </label>
                {isLoadingParticipants ? (
                  <div className="text-center py-4 text-gray-400">
                    <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {/* Header row */}
                    <div className="flex items-center justify-between px-2 py-1 text-xs text-gray-500 border-b border-gray-700">
                      <span>{t('profitDistribution.participant') || 'Участник'}</span>
                      <div className="flex items-center gap-4">
                        <span className="w-20 text-center">%</span>
                        <span className="w-28 text-right">{t('profitDistribution.payout') || 'Выплата'}</span>
                      </div>
                    </div>
                    
                    {profitParticipants.map((p, idx) => {
                      const amount = parseFloat(profitAmount) || 0;
                      const payoutAmount = (amount * p.percentage) / 100;
                      
                      return (
                        <div key={p.id} className="flex items-center justify-between p-2 bg-gray-900/50 rounded hover:bg-gray-900/70 transition-colors">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              p.type === 'user' ? 'bg-blue-600' : 'bg-orange-600'
                            } text-white`}>
                              {p.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-300 truncate">{p.name}</div>
                              <div className="text-xs text-gray-500">
                                {p.type === 'user' ? t('profitDistribution.manager') || 'Управляющий' : t('profitDistribution.artist') || 'Исполнитель'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={p.percentage}
                                onChange={(e) => {
                                  const newParticipants = [...profitParticipants];
                                  newParticipants[idx].percentage = parseFloat(e.target.value) || 0;
                                  setProfitParticipants(newParticipants);
                                }}
                                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-right text-sm focus:border-purple-500 focus:outline-none"
                                min="0"
                                max="100"
                                step="0.1"
                              />
                              <span className="text-gray-400 text-sm">%</span>
                            </div>
                            <div className="w-28 text-right">
                              <span className={`font-medium ${payoutAmount > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                                {getCurrencySymbol(currency)}{payoutAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {profitParticipants.length === 0 && (
                      <p className="text-center py-4 text-gray-500">
                        {t('profitDistribution.noParticipants') || 'Нет участников для выбранного пресета'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Total percentage and amount summary */}
              {profitParticipants.length > 0 && (
                <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className={`text-sm font-medium ${
                      Math.abs(profitParticipants.reduce((sum, p) => sum + p.percentage, 0) - 100) < 0.1 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {t('profitDistribution.totalPercentage') || 'Всего'}: {profitParticipants.reduce((sum, p) => sum + p.percentage, 0).toFixed(1)}%
                      {Math.abs(profitParticipants.reduce((sum, p) => sum + p.percentage, 0) - 100) >= 0.1 && 
                        <span className="ml-2 text-xs">({t('profitDistribution.mustBe100') || 'должно быть 100%'})</span>
                      }
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-400">{t('profitDistribution.totalPayout') || 'Итого к выплате'}:</span>
                      <span className="ml-2 font-bold text-purple-400">
                        {getCurrencySymbol(currency)}{((parseFloat(profitAmount) || 0) * profitParticipants.reduce((sum, p) => sum + p.percentage, 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  
                  {/* Quick distribution buttons */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        const equalShare = 100 / profitParticipants.length;
                        setProfitParticipants(profitParticipants.map(p => ({ ...p, percentage: equalShare })));
                      }}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                    >
                      {t('profitDistribution.equalShares') || 'Поровну'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setProfitParticipants(profitParticipants.map(p => ({ ...p, percentage: 0 })));
                      }}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                    >
                      {t('profitDistribution.resetAll') || 'Сбросить'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsProfitModalOpen(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handleCreateDistribution}
                disabled={!profitAmount || profitParticipants.length === 0 || 
                  (profitPreset === 'CUSTOM' && Math.abs(profitParticipants.reduce((sum, p) => sum + p.percentage, 0) - 100) >= 0.1)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {t('profitDistribution.create') || 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-white mb-4">
              {t('transactions.addTransaction') || 'Добавить транзакцию'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.date') || 'Дата'}
                </label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {getCategoryLabelKey()}
                </label>
                <select
                  value={formCategory}
                  onChange={(e) => handleCategoryChange(e.target.value as Transaction['category'])}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                >
                  <option value="CLIENT_DEPOSIT">{getCategoryLabel('CLIENT_DEPOSIT')}</option>
                  <option value="ARTIST_PAYOUT">{getCategoryLabel('ARTIST_PAYOUT')}</option>
                  <option value="SOFTWARE_HARDWARE">{getCategoryLabel('SOFTWARE_HARDWARE')}</option>
                  <option value="OTHER">{getCategoryLabel('OTHER')}</option>
                </select>
              </div>

              {formCategory === 'OTHER' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('transactions.type') || 'Тип'}
                  </label>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value as 'IN' | 'OUT')}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="IN">{t('transactions.in') || 'Приход'}</option>
                    <option value="OUT">{t('transactions.out') || 'Расход'}</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.amount') || 'Сумма'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  placeholder="0.00"
                />
              </div>

              {/* Coverage visualization for CLIENT_DEPOSIT */}
              {formCategory === 'CLIENT_DEPOSIT' && formAmount && parseFloat(formAmount) > 0 && liabilities.length > 0 && (() => {
                const coverage = calculateCoverage(parseFloat(formAmount));
                return (
                  <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3">
                      {t('transactions.coveragePreview') || 'Покрытие обязательств'}
                    </h3>
                    
                    {coverage.covered.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs text-green-400 mb-2 font-medium">
                          ✓ {t('transactions.fullyCovered') || 'Полностью покрыты'} ({coverage.covered.length})
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {coverage.covered.map(liability => (
                            <div key={liability.id} className="text-xs text-gray-300 bg-green-900/20 px-2 py-1 rounded">
                              {liability.description} — {getCurrencySymbol(currency)}{liability.amount.toLocaleString()}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {coverage.partial && (
                      <div className="mb-3">
                        <div className="text-xs text-yellow-400 mb-2 font-medium">
                          ⚠ {t('transactions.partiallyCovered') || 'Частично покрыто'}
                        </div>
                        <div className="text-xs text-gray-300 bg-yellow-900/20 px-2 py-1 rounded">
                          {coverage.partial.liability.description} — {getCurrencySymbol(currency)}{coverage.partial.amount.toLocaleString()} / {getCurrencySymbol(currency)}{coverage.partial.liability.amount.toLocaleString()}
                        </div>
                      </div>
                    )}

                    {coverage.remaining > 0 && (
                      <div className="text-xs text-gray-400 mt-2">
                        {t('transactions.remainingAfterCoverage') || 'Остаток после покрытия'}: {getCurrencySymbol(currency)}{coverage.remaining.toLocaleString()}
                      </div>
                    )}

                    {coverage.covered.length === 0 && !coverage.partial && (
                      <div className="text-xs text-gray-400">
                        {t('transactions.noCoverage') || 'Этот транш не покроет обязательства'}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.description') || 'Описание'}
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  rows={3}
                  placeholder={t('transactions.descriptionPlaceholder') || 'Например: Приход от Кира: 500к'}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handleCreateTransaction}
                disabled={!formAmount || !formDescription.trim()}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.create') || 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {isEditModalOpen && editingTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-white mb-4">
              {t('transactions.editTransaction') || 'Редактировать транзакцию'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.date') || 'Дата'}
                </label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {getCategoryLabelKey()}
                </label>
                <select
                  value={formCategory}
                  onChange={(e) => handleCategoryChange(e.target.value as Transaction['category'])}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                >
                  <option value="CLIENT_DEPOSIT">{getCategoryLabel('CLIENT_DEPOSIT')}</option>
                  <option value="ARTIST_PAYOUT">{getCategoryLabel('ARTIST_PAYOUT')}</option>
                  <option value="SOFTWARE_HARDWARE">{getCategoryLabel('SOFTWARE_HARDWARE')}</option>
                  <option value="OTHER">{getCategoryLabel('OTHER')}</option>
                </select>
              </div>

              {formCategory === 'OTHER' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('transactions.type') || 'Тип'}
                  </label>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value as 'IN' | 'OUT')}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="IN">{t('transactions.in') || 'Приход'}</option>
                    <option value="OUT">{t('transactions.out') || 'Расход'}</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.amount') || 'Сумма'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.description') || 'Описание'}
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingTransaction(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handleUpdateTransaction}
                disabled={!formAmount || !formDescription.trim()}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.save') || 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Liability Modal */}
      {isPayModalOpen && payingLiability && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-white mb-4">
              {t('transactions.payLiability') || 'Оплатить обязательство'}
            </h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
                <div className="text-sm text-gray-400 mb-1">
                  {t('transactions.description') || 'Описание'}
                </div>
                <div className="text-white">{payingLiability.description}</div>
              </div>

              <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
                <div className="text-sm text-gray-400 mb-1">
                  {t('transactions.totalAmount') || 'Общая сумма'}
                </div>
                <div className="text-lg font-bold text-white">
                  {getCurrencySymbol(currency)}{payingLiability.amount.toLocaleString()}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('transactions.payAmount') || 'Сумма оплаты'}
                </label>
                <input
                  type="number"
                  min="0"
                  max={payingLiability.amount}
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  placeholder={payingLiability.amount.toString()}
                />
                <div className="text-xs text-gray-400 mt-1">
                  {t('transactions.partialPaymentHint') || 'Оставьте пустым для полной оплаты'}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsPayModalOpen(false);
                  setPayingLiability(null);
                  setPayAmount('');
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handlePaySubmit}
                disabled={payAmount !== '' && (parseFloat(payAmount) <= 0 || parseFloat(payAmount) > payingLiability.amount)}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('transactions.pay') || 'Оплатить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay All Modal */}
      {isPayAllModalOpen && payAllItems.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <ICONS.Check className="w-6 h-6 text-green-400" />
              {t('transactions.payAll') || 'Выплатить всё'}
            </h2>

            <div className="space-y-4">
              {/* Artist info */}
              <div className="flex items-center gap-3 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold">
                  {payAllArtistName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-lg font-semibold text-white">{payAllArtistName}</div>
                  <div className="text-sm text-gray-400">
                    {payAllItems.length} {t('transactions.tasks') || 'задач'}
                  </div>
                </div>
              </div>

              {/* Items list */}
              <div className="max-h-48 overflow-y-auto space-y-1">
                {payAllItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between py-2 px-3 bg-gray-900/30 rounded text-sm">
                    <span className="text-gray-300 truncate flex-1 mr-2">
                      {item.shotName || `Shot ${item.shotNumber}`} — {item.specialization}
                    </span>
                    <span className="text-orange-400 font-medium whitespace-nowrap">
                      {getCurrencySymbol(currency)}{item.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-green-900/20 rounded-lg border border-green-700/50">
                <span className="text-gray-300 font-medium">
                  {t('transactions.totalAmount') || 'Общая сумма'}:
                </span>
                <span className="text-2xl font-bold text-green-400">
                  {getCurrencySymbol(currency)}{payAllItems.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsPayAllModalOpen(false);
                  setPayAllItems([]);
                  setPayAllArtistName('');
                }}
                disabled={isPayingAll}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handlePayAllConfirm}
                disabled={isPayingAll}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isPayingAll ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('common.processing') || 'Обработка...'}
                  </>
                ) : (
                  <>
                    <ICONS.Check className="w-4 h-4" />
                    {t('transactions.payAll') || 'Выплатить всё'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Liability Modal */}
      {isDeleteLiabilityModalOpen && deletingLiability && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <ICONS.Trash className="w-6 h-6 text-red-400" />
              {t('transactions.deleteLiability') || 'Удалить обязательство'}
            </h2>

            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-red-400 mt-0.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="text-sm text-red-300">
                    {t('transactions.deleteWarning') || 'Это действие нельзя отменить. Обязательство будет удалено безвозвратно.'}
                  </div>
                </div>
              </div>

              {/* Liability info */}
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                <div className="text-sm text-gray-400 mb-2">
                  {t('transactions.description') || 'Описание'}
                </div>
                <div className="text-white font-medium mb-3">
                  {deletingLiability.description}
                </div>
                {deletingLiability.artistName && (
                  <div className="text-sm text-gray-400 mb-1">
                    {t('transactions.artist') || 'Артист'}: <span className="text-white">{deletingLiability.artistName}</span>
                  </div>
                )}
                {deletingLiability.shotName && (
                  <div className="text-sm text-gray-400 mb-1">
                    {t('bidding.shot') || 'Шот'}: <span className="text-white">{deletingLiability.shotName}</span>
                  </div>
                )}
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between p-4 bg-red-900/20 rounded-lg border border-red-700/50">
                <span className="text-gray-300 font-medium">
                  {t('transactions.amount') || 'Сумма'}:
                </span>
                <span className="text-2xl font-bold text-red-400">
                  {getCurrencySymbol(currency)}{deletingLiability.amount.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsDeleteLiabilityModalOpen(false);
                  setDeletingLiability(null);
                }}
                disabled={isDeletingLiability}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handleDeleteLiabilityConfirm}
                disabled={isDeletingLiability}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeletingLiability ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('common.deleting') || 'Удаление...'}
                  </>
                ) : (
                  <>
                    <ICONS.Trash className="w-4 h-4" />
                    {t('common.delete') || 'Удалить'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Transaction Modal */}
      {isDeleteTransactionModalOpen && deletingTransactionId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <ICONS.Trash className="w-6 h-6 text-red-400" />
              {t('transactions.deleteTransaction') || 'Удалить транзакцию'}
            </h2>

            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-red-400 mt-0.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="text-sm text-red-300">
                    {t('transactions.deleteTransactionWarning') || 'Это действие нельзя отменить. Транзакция будет удалена безвозвратно.'}
                  </div>
                </div>
              </div>

              <div className="text-center text-gray-300">
                {t('transactions.confirmDeleteTransaction') || 'Вы уверены, что хотите удалить эту транзакцию?'}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsDeleteTransactionModalOpen(false);
                  setDeletingTransactionId(null);
                }}
                disabled={isDeletingTransaction}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handleDeleteTransactionConfirm}
                disabled={isDeletingTransaction}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeletingTransaction ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('common.deleting') || 'Удаление...'}
                  </>
                ) : (
                  <>
                    <ICONS.Trash className="w-4 h-4" />
                    {t('common.delete') || 'Удалить'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revert Payment Modal */}
      {isRevertPaymentModalOpen && revertingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <ICONS.RefreshCcw className="w-6 h-6 text-yellow-400" />
              {t('transactions.revertPayment') || 'Отменить выплату'}
            </h2>

            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-yellow-400 mt-0.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="text-sm text-yellow-300">
                    {t('transactions.revertPaymentWarning') || 'Выплата будет отменена и вернётся в список ожидающих обязательств.'}
                  </div>
                </div>
              </div>

              {/* Payment info */}
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                <div className="text-sm text-gray-400 mb-2">
                  {t('transactions.description') || 'Описание'}
                </div>
                <div className="text-white font-medium mb-3">
                  {revertingPayment.description}
                </div>
                {revertingPayment.artistName && (
                  <div className="text-sm text-gray-400 mb-1">
                    {t('transactions.artist') || 'Артист'}: <span className="text-white">{revertingPayment.artistName}</span>
                  </div>
                )}
                {revertingPayment.shotName && (
                  <div className="text-sm text-gray-400 mb-1">
                    {t('bidding.shot') || 'Шот'}: <span className="text-white">{revertingPayment.shotName}</span>
                  </div>
                )}
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between p-4 bg-yellow-900/20 rounded-lg border border-yellow-700/50">
                <span className="text-gray-300 font-medium">
                  {t('transactions.amount') || 'Сумма'}:
                </span>
                <span className="text-2xl font-bold text-yellow-400">
                  {getCurrencySymbol(currency)}{revertingPayment.amount.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsRevertPaymentModalOpen(false);
                  setRevertingPayment(null);
                }}
                disabled={isRevertingPayment}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={handleRevertPaymentConfirm}
                disabled={isRevertingPayment}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isRevertingPayment ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('transactions.reverting') || 'Отмена...'}
                  </>
                ) : (
                  <>
                    <ICONS.RefreshCcw className="w-4 h-4" />
                    {t('transactions.revert') || 'Отменить выплату'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Distribution Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteDistributionModal.isOpen}
        onClose={() => setDeleteDistributionModal({ isOpen: false, distributionId: '', isDistributed: false })}
        onConfirm={confirmDeleteDistribution}
        title={t('profitDistribution.deleteTitle') || 'Удалить распределение'}
        message={deleteDistributionModal.isDistributed 
          ? t('profitDistribution.confirmDeleteDistributed') || 'Удалить это распределение? Все связанные транзакции выплаты прибыли также будут удалены.'
          : t('profitDistribution.confirmDelete') || 'Удалить это распределение прибыли?'}
        confirmText={t('common.delete') || 'Удалить'}
        cancelText={t('common.cancel') || 'Отмена'}
        type="danger"
      />
    </div>
  );
};

export default TransactionsPage;


